<template>
  <Teleport to="body">
    <div class="sheet-backdrop" @click.self="$emit('close')">
      <div class="sheet solved" role="dialog" aria-label="Solved">
        <button class="sheet__close" aria-label="Close" @click="$emit('close')">✕</button>
        <p class="solved__eyebrow">{{ clean ? 'Solved clean' : 'Solved' }}</p>
        <h2 class="solved__title">{{ puzzle.title }}</h2>
        <p class="solved__time">{{ time }}<span v-if="inRoom" class="muted"> · together</span></p>
        <p class="solved__theme">{{ puzzle.theme.explanation }}</p>
        <ul class="solved__entries">
          <li v-for="e in puzzle.theme.entries" :key="e.answer">{{ e.display }}</li>
        </ul>

        <div v-if="!puzzle.giant" class="solved__rate">
          <p class="solved__rate-q">{{ rated ? 'Thanks - noted.' : `How hard was it, for a ${puzzle.weekday || 'puzzle'}?` }}</p>
          <div class="solved__votes" role="group" aria-label="How hard was it?">
            <button
              v-for="v in votes"
              :key="v.id"
              class="solved__vote"
              :class="{ 'is-on': vote === v.id }"
              :aria-pressed="vote === v.id"
              @click="rate(v.id)"
            >{{ v.label }}</button>
          </div>
        </div>

        <div v-if="rows.length" class="solved__times">
          <h3>{{ puzzle.giant ? 'Everyone’s times' : 'Today’s times' }}</h3>
          <ol>
            <li v-for="row in rows" :key="row.uid" :class="{ 'is-me': row.uid === myUid }">
              <span class="solved__name">{{ row.name }}</span>
              <span class="solved__t">{{ fmt(row.seconds) }}<small v-if="!row.clean"> with help</small><small v-else-if="row.together"> together</small></span>
            </li>
          </ol>
        </div>

        <div class="solved__actions">
          <button class="secondary" @click="shareTime">{{ copied ? 'Copied' : 'Share my time' }}</button>
          <button class="primary" @click="$emit('close')">Nice</button>
        </div>
      </div>
    </div>
  </Teleport>
</template>

<script>
// What the theme was, how long it took, and how everyone else did today.
import { mapState, mapGetters } from 'vuex';
import { formatTime } from '@/engine/solving';
import { shareResultText } from '@/utils/shareResult';
import { VOTES } from '@/store/ratings';

export default {
  name: 'SolvedSheet',
  emits: ['close'],
  data() {
    return {
      copied: false,
      votes: VOTES,
      vote: null, // my difficulty vote; changeable while the sheet is open
      rated: false, // voted on this opening (for the thanks)
      // Phones get the share menu, computers copy - as ShareSheet does.
      touch: typeof window !== 'undefined' && Boolean(window.matchMedia?.('(pointer: coarse)').matches),
    };
  },
  computed: {
    ...mapState(['puzzle', 'seconds', 'times']),
    ...mapGetters(['helpUsed', 'inRoom', 'myUid']),
    clean() { return !this.helpUsed; },
    time() { return formatTime(this.seconds); },
    rows() {
      return Object.entries(this.times)
        .map(([uid, t]) => ({ uid, ...t }))
        .sort((a, b) => a.seconds - b.seconds);
    },
  },
  async mounted() {
    const vote = await this.$store.dispatch('loadMyRating');
    if (vote && !this.vote) this.vote = vote;
  },
  methods: {
    fmt: formatTime,
    rate(vote) {
      this.vote = vote;
      this.rated = true;
      this.$store.dispatch('rateDifficulty', vote);
    },
    // Spoiler-free: the day, the title, the time, and a link to this puzzle.
    async shareTime() {
      const text = shareResultText({
        puzzle: this.puzzle, seconds: this.seconds, clean: this.clean, together: this.inRoom, origin: window.location.origin,
      });
      if (this.touch && navigator.share) {
        try { await navigator.share({ text }); } catch { /* dismissed */ }
        return;
      }
      try {
        await navigator.clipboard.writeText(text);
        this.copied = true;
      } catch { /* no clipboard */ }
    },
  },
};
</script>

<style lang="scss" scoped>
.solved { text-align: center; }
.solved__eyebrow { margin: 0; font-size: 0.75rem; letter-spacing: 0.1em; text-transform: uppercase; color: var(--accent); font-weight: 800; }
.solved__title { margin: 0.3rem 0 0.2rem; font-size: 1.5rem; }
.solved__time { margin: 0; font-size: 2rem; font-weight: 800; font-variant-numeric: tabular-nums; }
.solved__theme { color: var(--ink-dim); font-size: 0.92rem; margin: 0.8rem 0 0.4rem; }
.solved__entries { list-style: none; margin: 0 0 0.9rem; padding: 0; display: flex; flex-wrap: wrap; justify-content: center; gap: 0.35rem;
  li { background: var(--accent); color: #211a05; font-weight: 800; font-size: 0.78rem; letter-spacing: 0.05em; padding: 0.2rem 0.55rem; border-radius: 999px; } }
.solved__rate { margin: 0 0 0.9rem; }
.solved__rate-q { margin: 0 0 0.4rem; font-size: 0.85rem; color: var(--ink-dim); }
.solved__votes { display: flex; gap: 0.35rem; }
.solved__vote { flex: 1; width: auto; margin: 0; padding: 0.45rem 0.3rem; font-size: 0.82rem; font-weight: 700;
  background: transparent; color: var(--ink); border: 1px solid var(--edge); border-radius: 999px;
  &.is-on { background: var(--accent); border-color: var(--accent); color: #211a05; } }
.solved__times { text-align: left; border-top: 1px solid var(--edge); padding-top: 0.6rem; margin-bottom: 0.9rem;
  h3 { margin: 0 0 0.3rem; font-size: 0.75rem; letter-spacing: 0.08em; text-transform: uppercase; color: var(--ink-dim); }
  ol { list-style: none; margin: 0; padding: 0; }
  li { display: flex; justify-content: space-between; padding: 0.2rem 0; font-size: 0.9rem; &.is-me { color: var(--accent); font-weight: 700; } }
  small { color: var(--ink-dim); font-weight: 400; } }
.solved__actions { display: flex; gap: 0.5rem; .primary, .secondary { flex: 1; width: auto; margin: 0; } }
.solved__t { font-variant-numeric: tabular-nums; font-weight: 700; }
</style>
